'use client';

import {ModalBackdrop} from 'common/components/modal_backdrop';
import {useState} from 'react';

// Acknowledging "don't show again" persists globally so the warning isn't shown on every download.
const ACK_KEY = 'iq:takehome-file-download-ack';

export function hasAckedFileDownload(): boolean {
  return typeof window !== 'undefined' && localStorage.getItem(ACK_KEY) === '1';
}

type Props = {
  url: string;
  onClose: () => void;
};

export function FileDownloadWarningModal({url, onClose}: Props) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  function handleDownload() {
    if (dontShowAgain) localStorage.setItem(ACK_KEY, '1');
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  }

  return (
    <ModalBackdrop onClose={onClose} aria-label='File download warning'>
      <div className='relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-4'>
        <h3 className='text-lg font-semibold text-iq-black'>Before you download</h3>
        <p className='text-sm text-iq-grey-medium leading-relaxed'>
          User-uploaded files are scanned and sanitized, but downloading any user-submitted file
          still carries some risk. Continue only if you trust the source and keep your device&apos;s
          security software up to date.
        </p>
        <label className='flex items-center gap-2 text-sm text-iq-grey-dark cursor-pointer'>
          <input
            type='checkbox'
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className='w-4 h-4 accent-iq-black'
          />
          Don&apos;t show this warning again
        </label>
        <div className='flex justify-end gap-2'>
          <button
            type='button'
            onClick={onClose}
            className='py-2 px-4 border border-iq-grey-light text-iq-black text-sm font-semibold rounded-lg hover:bg-iq-grey-lighter transition-colors'>
            Cancel
          </button>
          <button
            type='button'
            onClick={handleDownload}
            className='py-2 px-4 bg-iq-black text-white text-sm font-semibold rounded-lg hover:opacity-80 transition-opacity'>
            Download file
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
